import {
	type Component,
	createEffect,
	createSignal,
	type JSX,
	Match,
	onMount,
	Show,
	Switch,
	useContext
} from "solid-js";
import { twJoin } from "tailwind-merge";
import { PricingPageMethods } from "~/solidJs/pages/PricingPage";
import { useCustomContext, usePriceAnimation } from "~/solidJs/shared";
import { SubscriptionLength } from "~/solidJs/shared/models/pageParams/pricingPageParams";
import { PremiumPopUpContext } from "../PremiumPopUp/models/premiumPopUpContext";

type PriceDisplayerProps = {
	price: string;
	priceRu: string;
	align?: "center" | "left";
	basePrice?: string;
	basePriceRu?: string;
	isFree?: boolean;
	size?: "medium" | "large";
	isSelected?: boolean;
	isHiddenMonth?: boolean;
	isNeedToHideCurrency?: boolean;
	canApplyPromoCode?: boolean;
};

type LineThroughV2Props = {
	children: JSX.Element;
	isSelected?: boolean;
};

const LineThroughV2: Component<LineThroughV2Props> = (props) => (
	<div
		class={twJoin(
			"font-medium text-base line-through",
			props.isSelected ? "text-[rgb(255,255,255)]/80" : "text-text-secondary"
		)}
	>
		{props.children}
	</div>
);

type LineThroughV1Props = {
	children: JSX.Element;
	isSelected?: boolean;
};

const LineThroughV1: Component<LineThroughV1Props> = (props) => (
	<div
		class={twJoin(
			"font-medium text-sm line-through",
			props.isSelected ? "text-gray-300" : "text-text-secondary"
		)}
	>
		{props.children}
	</div>
);

export const PriceDisplayerV2: Component<PriceDisplayerProps> = (props) => {
	const premiumPopUpStore = useContext(PremiumPopUpContext);

	const promoPercentageRu = () => {
		const store = premiumPopUpStore?.getPremiumPopUpStore();
		const isRuCards = store?.isRuCards;
		const isPromo = store?.promocodeChange?.amount;
		if (!isRuCards || !isPromo) return null;
		const basePrice = Number(props.priceRu);
		if (Number.isNaN(basePrice)) return;
		return (basePrice * ((100 - isPromo) / 100)).toFixed(0);
	};

	const promoPercentageNonRu = () => {
		const store = premiumPopUpStore?.getPremiumPopUpStore();
		const isRuCards = store?.isRuCards;
		const isPromo = store?.promocodeChange?.amount;
		if (isRuCards || !isPromo) return null;
		const basePrice = Number(props.price);
		if (Number.isNaN(basePrice)) return;
		return (basePrice * ((100 - isPromo) / 100)).toFixed(2);
	};
	return (
		<div
			data-test={"premium-price-displayer"}
			class={twJoin(
				"flex w-full flex-col items-start gap-1",
				props.align === "center" && "justify-center",
				props.size === "medium" && "text-xl",
				props.size === "large" && "text-3xl",
				!props.size && "text-3xl"
			)}
		>
			<div
				class={twJoin(
					"flex flex-wrap items-end gap-1 font-semibold text-3xl leading-8",
					props.isSelected ? "text-white" : "text-text-primary"
				)}
				style={{ "font-variant-numeric": "tabular-nums" }}
			>
				<Switch fallback={`$${props.price}`}>
					<Match
						when={props.isFree && premiumPopUpStore?.getPremiumPopUpStore().isRuCards}
					>
						0 ₽
					</Match>
					<Match when={promoPercentageRu()} keyed>
						{promoPercentageRu()} ₽
						<LineThroughV2
							isSelected={props.isSelected}
						>{`${props.priceRu} ₽`}</LineThroughV2>
					</Match>
					<Match when={promoPercentageNonRu()} keyed>
						${promoPercentageNonRu()}
						<LineThroughV2
							isSelected={props.isSelected}
						>{`$${props.price}`}</LineThroughV2>
					</Match>
					<Match when={props.isNeedToHideCurrency}>{props.price}</Match>
					<Match
						when={
							premiumPopUpStore?.getPremiumPopUpStore().isRuCards &&
							props.isNeedToHideCurrency
						}
					>
						{props.priceRu}
					</Match>

					<Match when={props.isFree}>$0</Match>
					<Match
						when={
							premiumPopUpStore?.getPremiumPopUpStore().isRuCards && props.basePriceRu
						}
					>
						{props.priceRu} ₽
						<LineThroughV2
							isSelected={props.isSelected}
						>{`${props.basePriceRu} ₽`}</LineThroughV2>
					</Match>
					<Match when={premiumPopUpStore?.getPremiumPopUpStore().isRuCards}>
						{props.priceRu} ₽
					</Match>
					<Match when={props.basePrice}>
						${props.price}
						<LineThroughV2
							isSelected={props.isSelected}
						>{`$${props.basePrice}`}</LineThroughV2>
					</Match>
				</Switch>
			</div>

			<div
				class={twJoin(
					"font-medium text-2xs leading-normal",
					props.isSelected ? "text-gray-300" : "text-text-secondary",
					props.isHiddenMonth && "opacity-0"
				)}
			>
				/month
			</div>
		</div>
	);
};

export const PriceDisplayerV3: Component<PriceDisplayerProps> = (props) => {
	const pricingStore = useCustomContext(PricingPageMethods.PricingPageContext);
	const state = useCustomContext(PricingPageMethods.PricingPageContext);

	// Virtual element for measuring text width
	let measureRef: HTMLSpanElement | undefined;
	const [textWidth, setTextWidth] = createSignal(0);

	const getCurrentPrice = () => {
		const isRuCards = pricingStore?.getIsRuCards();
		const isLengthYear = pricingStore?.getIsLengthYear();
		if (isRuCards) {
			if (!isLengthYear) {
				return props.priceRu;
			}
			return props.basePriceRu;
		}
		if (!isLengthYear) {
			return props.price;
		}
		return props.basePrice;
	};

	const promoPercentageRu = () => {
		const isRuCards = pricingStore?.getIsRuCards();
		const isPromo = pricingStore?.usePromocode?.getPromoCode()?.amount;
		if (!isRuCards || !isPromo || !props.canApplyPromoCode) return null;
		const basePrice = Number(getCurrentPrice());
		if (Number.isNaN(basePrice)) return;
		return (basePrice * ((100 - isPromo) / 100)).toFixed(0);
	};

	const promoPercentageNonRu = () => {
		const isRuCards = pricingStore?.getIsRuCards();
		const isPromo = pricingStore?.usePromocode?.getPromoCode()?.amount;
		if (isRuCards || !isPromo || !props.canApplyPromoCode) return null;
		const basePrice = Number(getCurrentPrice());
		if (Number.isNaN(basePrice)) return;
		return (basePrice * ((100 - isPromo) / 100)).toFixed(2);
	};

	const getAnnualPriceWithPromo = () => {
		const isRuCards = pricingStore?.getIsRuCards();
		const promoCode = pricingStore?.usePromocode?.getPromoCode();
		const isPromo = props.canApplyPromoCode && promoCode?.amount;

		if (!isPromo) {
			// Return original price if no promo
			return pricingStore.getIsRuCards() ? `${props.priceRu} ₽` : `$${props.price}`;
		}

		// Calculate discounted annual price
		const annualPrice = isRuCards ? Number(props.priceRu) : Number(props.price);
		if (Number.isNaN(annualPrice)) {
			return pricingStore.getIsRuCards() ? `${props.priceRu} ₽` : `$${props.price}`;
		}

		const discountedPrice = annualPrice * ((100 - isPromo) / 100);
		const formattedPrice = isRuCards ? discountedPrice.toFixed(0) : discountedPrice.toFixed(2);
		const priceText = isRuCards ? `${formattedPrice} ₽` : `$${formattedPrice}`;

		return priceText;
	};

	// Определяем текущую активную цену и валюту для анимации
	const currentPriceInfo = () => {
		const isRuCards = pricingStore?.getIsRuCards();
		const isLengthYear = pricingStore?.getIsLengthYear();

		// Бесплатная цена
		if (props.isFree) {
			return {
				price: "0",
				currency: isRuCards ? "₽" : "$",
				isRu: isRuCards
			};
		}

		// Цена с промокодом (рубли)
		const promoRu = promoPercentageRu();
		if (promoRu) {
			return {
				price: promoRu,
				currency: "₽",
				isRu: true
			};
		}

		// Цена с промокодом (доллары)
		const promoNonRu = promoPercentageNonRu();
		if (promoNonRu) {
			return {
				price: promoNonRu,
				currency: "$",
				isRu: false
			};
		}

		// Цена без валюты
		if (props.isNeedToHideCurrency) {
			return {
				price: isRuCards ? props.basePriceRu : props.basePrice,
				currency: "",
				isRu: isRuCards
			};
		}
		const price = getCurrentPrice();
		// Обычная цена с валютой
		if (isRuCards) {
			if (!isLengthYear) {
				return {
					price: price,
					currency: "₽",
					isRu: true
				};
			}
			return {
				price: price,
				currency: "₽",
				isRu: true
			};
		}
		if (!isLengthYear) {
			return {
				price: price,
				currency: "$",
				isRu: false
			};
		}
		return {
			price: price,
			currency: "$",
			isRu: false
		};
	};

	const priceAnimation = usePriceAnimation(() => currentPriceInfo().price, {
		duration: 600
	});

	const formattedAnimatedPrice = () => {
		const info = currentPriceInfo();
		const animatedValue = priceAnimation.animatedValue();

		if (info.currency === "") {
			return animatedValue;
		}

		return info.currency === "₽" ? `${animatedValue} ₽` : `$${animatedValue}`;
	};

	const nxtPriceText = () => {
		const info = currentPriceInfo();
		return info.isRu ? `${info.price} ₽` : `$${info.price}`;
	};

	// Update text width when price changes
	const updateTextWidth = () => {
		if (measureRef) {
			const width = measureRef.getBoundingClientRect().width;
			setTextWidth(width);
		}
	};

	// Measure width on mount and when price changes
	onMount(() => {
		updateTextWidth();
		// Update width on resize to handle responsive font sizes
		const resizeObserver = new ResizeObserver(updateTextWidth);
		if (measureRef) {
			resizeObserver.observe(measureRef);
		}
		return () => resizeObserver.disconnect();
	});

	// Update width when price text changes
	createEffect(() => {
		// Track dependencies
		nxtPriceText();
		// Update width when text changes
		updateTextWidth();
	});

	const priceClasses =
		"xl:header-48-medium shrink-0 whitespace-nowrap font-medium text-4xl leading-[44px] text-text-primary";

	return (
		<div translate="no" class="flex h-[50px] items-end gap-1.5">
			{/* Virtual element for measuring text width */}
			<span
				ref={measureRef}
				class={twJoin(priceClasses, "invisible absolute")}
				style={{
					"font-variant-numeric": "tabular-nums",
					"pointer-events": "none"
				}}
			>
				{nxtPriceText()}
			</span>

			<span
				class={twJoin(priceClasses)}
				style={{
					"font-variant-numeric": "tabular-nums",
					width: `${textWidth()}px`,
					transition: "opacity, width 0.6s ease"
				}}
			>
				{formattedAnimatedPrice()}
			</span>

			<div class="flex h-full flex-col justify-end gap-0.5 pb-1">
				<span class="caption-12-medium text-text-secondary">/ month</span>
				<Show
					when={
						props.isFree === false &&
						state.getSubscriptionLength() === SubscriptionLength.Year
					}
				>
					<span class="caption-12-medium text-text-secondary">
						billed annually as {getAnnualPriceWithPromo()}
					</span>
				</Show>
			</div>
		</div>
	);
};

export const PriceDisplayerV1: Component<PriceDisplayerProps> = (props) => {
	const premiumPopUpStore = useContext(PremiumPopUpContext);

	const promoPercentageRu = () => {
		const store = premiumPopUpStore?.getPremiumPopUpStore();
		const isRuCards = store?.isRuCards;
		const isPromo = store?.promocodeChange?.amount;
		if (!isRuCards || !isPromo) return null;
		const basePrice = Number(props.priceRu);
		if (Number.isNaN(basePrice)) return;
		return (basePrice * ((100 - isPromo) / 100)).toFixed(0);
	};

	const promoPercentageNonRu = () => {
		const store = premiumPopUpStore?.getPremiumPopUpStore();
		const isRuCards = store?.isRuCards;
		const isPromo = store?.promocodeChange?.amount;
		if (isRuCards || !isPromo) return null;
		const basePrice = Number(props.price);
		if (Number.isNaN(basePrice)) return;
		return (basePrice * ((100 - isPromo) / 100)).toFixed(2);
	};
	return (
		<div
			data-test={"premium-price-displayer"}
			class={twJoin(
				"flex w-full flex-col items-start whitespace-nowrap",
				props.align === "center" && "justify-center",
				props.size === "medium" && "text-xl",
				props.size === "large" && "text-3xl",
				!props.size && "text-3xl"
			)}
		>
			<div
				class={twJoin(
					"flex flex-wrap items-end gap-1 font-semibold",
					props.isSelected ? "text-white" : "text-text-primary"
				)}
				style={{ "font-variant-numeric": "tabular-nums" }}
			>
				<Switch fallback={`$${props.price}`}>
					<Match
						when={props.isFree && premiumPopUpStore?.getPremiumPopUpStore().isRuCards}
					>
						0 ₽
					</Match>
					<Match when={promoPercentageRu()} keyed>
						<LineThroughV1
							isSelected={props.isSelected}
						>{`${props.priceRu} ₽`}</LineThroughV1>
						{promoPercentageRu()} ₽
					</Match>
					<Match when={promoPercentageNonRu()} keyed>
						<LineThroughV1
							isSelected={props.isSelected}
						>{`$${props.price}`}</LineThroughV1>
						${promoPercentageNonRu()}
					</Match>
					<Match when={props.isNeedToHideCurrency}>{props.price}</Match>
					<Match
						when={
							premiumPopUpStore?.getPremiumPopUpStore().isRuCards &&
							props.isNeedToHideCurrency
						}
					>
						{props.priceRu}
					</Match>
					<Match when={props.isFree}>$0</Match>
					<Match
						when={
							premiumPopUpStore?.getPremiumPopUpStore().isRuCards && props.basePriceRu
						}
					>
						<LineThroughV1
							isSelected={props.isSelected}
						>{`${props.basePriceRu} ₽`}</LineThroughV1>
						{props.priceRu} ₽
					</Match>
					<Match when={premiumPopUpStore?.getPremiumPopUpStore().isRuCards}>
						{props.priceRu} ₽
					</Match>
					<Match when={props.basePrice}>
						<LineThroughV1
							isSelected={props.isSelected}
						>{`$${props.basePrice}`}</LineThroughV1>
						${props.price}
					</Match>
				</Switch>
			</div>

			<div
				class={twJoin(
					"h-max w-max text-sm",
					props.isSelected ? "text-gray-300" : "text-text-secondary"
				)}
			>
				/ Per month
			</div>
		</div>
	);
};
